import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "../../../utils/api";

const formatReviewMonth = (createdAt) => {
  if (!createdAt) return "";
  return new Date(createdAt).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

export const TutorReviewsSelectionSection = () => {
  const [reviews, setReviews] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadReviews = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await apiRequest("/api/v1/reviews/my/approved");
        if (cancelled) return;
        const list = Array.isArray(response) ? response : [];
        const displayed = list
          .filter((review) => review.displayOnProfile)
          .map((review) => review.id);
        setReviews(list);
        setSelectedIds(displayed);
        setSavedIds(displayed);
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message || "Unable to load approved reviews.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadReviews();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasChanges = useMemo(() => {
    const current = [...selectedIds].sort((a, b) => a - b).join(",");
    const saved = [...savedIds].sort((a, b) => a - b).join(",");
    return current !== saved;
  }, [selectedIds, savedIds]);

  const toggleReview = (reviewId) => {
    setMessage("");
    setError("");
    setSelectedIds((current) => {
      if (current.includes(reviewId)) {
        return current.filter((id) => id !== reviewId);
      }
      if (current.length >= 5) {
        setError("You can select up to 5 reviews.");
        return current;
      }
      return [...current, reviewId];
    });
  };

  const saveSelection = async () => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const response = await apiRequest(
        "/api/v1/reviews/my/profile-selection",
        {
          method: "PATCH",
          body: JSON.stringify({ reviewIds: selectedIds }),
        },
      );
      const list = Array.isArray(response) ? response : reviews;
      const displayed = list
        .filter((review) => review.displayOnProfile)
        .map((review) => review.id);
      setReviews(list);
      setSelectedIds(displayed);
      setSavedIds(displayed);
      setMessage("Profile review selection saved.");
    } catch (saveError) {
      setError(saveError.message || "Unable to save review selection.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 sm:pt-10">
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-brand-dark-blue sm:text-2xl">
              Reviews Displayed on Your Profile
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Select up to 5 approved reviews to display on your public profile.
            </p>
          </div>
          <span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-brand-medium-blue">
            {selectedIds.length}/5 selected
          </span>
        </div>

        {loading ? (
          <p className="py-8 text-center text-gray-500">Loading reviews...</p>
        ) : error && reviews.length === 0 ? (
          <p className="mt-5 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            {error}
          </p>
        ) : reviews.length === 0 ? (
          <p className="py-8 text-center text-gray-500">
            No approved reviews are available yet.
          </p>
        ) : (
          <div className="mt-6 space-y-3">
            {reviews.map((review) => {
              const selected = selectedIds.includes(review.id);
              return (
                <label
                  key={review.id}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
                    selected
                      ? "border-brand-medium-blue bg-blue-50"
                      : "border-gray-200 hover:border-blue-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleReview(review.id)}
                    className="mt-1 h-4 w-4 accent-blue-600"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-medium text-gray-500">
                      {formatReviewMonth(review.createdAt)}
                    </span>
                    <span className="mt-1 block text-sm text-gray-700">
                      {review.comment || "No comment provided."}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {error && reviews.length > 0 && (
          <p className="mt-4 text-sm text-red-600">{error}</p>
        )}
        {message && (
          <p className="mt-4 text-sm text-green-700">{message}</p>
        )}

        {reviews.length > 0 && (
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={saveSelection}
              disabled={!hasChanges || saving}
              className="w-full rounded-lg bg-brand-medium-blue px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              {saving ? "Saving..." : "Save Review Selection"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
